/**
 * Hook for fetching observations with embeddings
 */
import { useEffect, useState, useCallback } from 'react'
import type { Observation, ObservationsResponse } from '../types'
import { apiUrl } from '../lib/api'

interface UseObservationsOptions {
  limit?: number
  agent?: string
  obsType?: string
}

interface UseObservationsReturn {
  observations: Observation[]
  loading: boolean
  error: string | null
  refetch: () => void
}

export function useObservations(options: UseObservationsOptions = {}): UseObservationsReturn {
  const { limit = 100, agent, obsType } = options
  const [observations, setObservations] = useState<Observation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchObservations = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const params = new URLSearchParams()
      params.append('limit', limit.toString())
      if (agent) params.append('agent', agent)
      if (obsType) params.append('obs_type', obsType)
      
      const response = await fetch(apiUrl(`/api/observations?${params.toString()}`))
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data: ObservationsResponse = await response.json()
      
      // Ensure all observations have embeddings (even if empty)
      const processedObs = data.observations.map(obs => ({
        ...obs,
        embedding: obs.embedding || []
      }))
      
      setObservations(processedObs)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch observations')
      setObservations([])
    } finally {
      setLoading(false)
    }
  }, [limit, agent, obsType])

  useEffect(() => {
    fetchObservations()
    
    // Poll every 60 seconds for updates
    const interval = setInterval(fetchObservations, 60000)
    return () => clearInterval(interval)
  }, [fetchObservations])

  return {
    observations,
    loading,
    error,
    refetch: fetchObservations
  }
}
